import { Td, Tr } from 'app/components/Table';
import { ReactComponent as LineIcon } from 'resources/images/line.svg';
import { memo } from 'react';
import {
  ListItem,
  ListItemAvatar,
  ListItemText,
} from 'app/components/ChakraTableList';
import { getAvatarImage } from 'app/utils/utils';
import { Icon } from '@chakra-ui/react';
import { LatestSplit, Member } from 'types/splits.types';
import { ContributionClassRolesDDL } from 'types/contribution';
import { SplitStatus } from '../SplitStatus';

interface LatestSplitRowProps {
  split?: LatestSplit;
  member: Member;
  currentUserGuid?: string;
  contributionRolesDDL?: ContributionClassRolesDDL;
  proposeNewSplit?: () => void;
  acceptSplit?: () => void;
}

const LatestSplitRowComponent = ({
  split,
  member,
  currentUserGuid,
  contributionRolesDDL,
  proposeNewSplit,
  acceptSplit,
}: LatestSplitRowProps) => {
  const roles = member?.roles
    ?.map((role) => role && contributionRolesDDL?.[role]?.name)
    .join(', ');

  return (
    <Tr zIndex={1} position="relative" role="row">
      <Td isAvatar sx={{ overflow: 'visible' }}>
        <ListItemAvatar
          initials={member.profileCard?.initials}
          colors={member.profileCard?.avatarColors}
          src={getAvatarImage(member.profileCard?.profileImages)}
        />
      </Td>
      <Td sx={{ pl: 0 }}>
        <ListItemText
          data-testid="latest-split-display-name"
          primary={member.profileCard?.displayName}
          title={member.profileCard?.displayName}
          secondary={roles}
          secondaryTextProps={{
            fontSize: 'xs',
            color: 'textSubdued',
            title: roles,
            'data-testid': 'latest-split-roles',
          }}
        />
      </Td>
      <Td>
        <ListItemText
          data-testid="latest-split-percentage"
          primary={`${member.share}%`}
        />
      </Td>
      <Td
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        <SplitStatus
          proposeNewSplit={proposeNewSplit}
          acceptSplit={acceptSplit}
          split={split}
          currentUserGuid={currentUserGuid}
          member={member}
          data-testid="latest-split-status"
        />
      </Td>
      <Td>
        {member.ipi ? (
          <ListItemText data-testid="latest-split-ipi" primary={member.ipi} />
        ) : (
          <ListItem data-testid="latest-split-ipi">
            <Icon as={LineIcon} color="iconBase" width={2} />
          </ListItem>
        )}
      </Td>
      <Td className="gap" />
    </Tr>
  );
};

export const LatestSplitRow = memo(
  LatestSplitRowComponent,
  (prev: LatestSplitRowProps, curr: LatestSplitRowProps) => {
    const compare = (prev: any, curr: any) =>
      JSON.stringify(prev) === JSON.stringify(curr);

    return (
      prev.acceptSplit === curr.acceptSplit &&
      prev.proposeNewSplit === curr.proposeNewSplit &&
      compare(prev.contributionRolesDDL, curr.contributionRolesDDL) &&
      compare(prev.currentUserGuid, curr.currentUserGuid) &&
      compare(prev.member, curr.member)
    );
  }
);
